/*
 * Copyright (c) 2022. American Express Company
 */

// theirs
import { isSafari } from 'react-device-detect';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import $ from 'jquery';
import _ from 'lodash';
import Konva from 'konva';
import { Stage } from 'konva/lib/Stage';
import { Line } from 'konva/lib/shapes/Line';
import { Circle } from 'konva/lib/shapes/Circle';
import { Shape } from 'konva/lib/Shape';
import { Rect } from 'konva/lib/shapes/Rect';
import { Group } from 'konva/lib/Group';
import { Vector2d } from 'konva/lib/types';
import { RegularPolygon } from 'konva/lib/shapes/RegularPolygon';

// ours
import { CardModel } from 'src/types/card.model';
import TLPin from '../elements/TLPin';
import { EventSizes, JaegerService } from '../../../services/jaeger.service';
import UnitService from '../../../services/unit.service';
import CardService from '../../../services/card.service';
import { JaegerEvent } from '../data/JaegerEvent.enum';
import NavigationService from '../../../services/navigation.service';
import { KonvaEventObject } from 'konva/lib/Node';
import LiteralsService from '../../../services/literals/LiteralsService';
import { CursorStyle } from '../data/CursorStyle.enum';

// styles and icons
import colours from 'src/styles/variables.module.scss';
import { goalImages, goalImagesSafari } from 'src/bw/goal/Goal';
import { canHaveIncomeSources } from 'src/bw/factories/income.factory';
import WarningIcon from '../warning.filled.svg';

export default class PinFactory {
    private static instance: PinFactory;

    // prevent new with private constructor
    private constructor() {}

    public build(ctx: TLPin): void {
        this.upsertPinGroup(ctx);
        this.upsertEventLengthRect(ctx);
        this.upsertMainRect(ctx);
        this.upsertLine(ctx);
        this.setClickListener(ctx);
        this.setDragListener(ctx);
        this.setDragEndListener(ctx);
        this.setDragMoveListener(ctx);
        this.setContextMenuListener(ctx);
        this.createImage(ctx);
        if (ctx.startDate) {
            this.createAlertIcon(ctx);
            this.createPinDetails(ctx);
            this.createSavingsDetails(ctx);
        }

        ctx.timelineService.pinBoard.pinboardGroup.add(ctx.pinGroup);
    }

    public create(xPosition: number, yPosition: number, card: CardModel): TLPin {
        return new TLPin(xPosition, yPosition, card);
    }

    public onClick(ctx: TLPin): void {
        ctx.pinGroup.moveToTop();
        NavigationService.goTo(
            ctx.timelineService.getProps().history,
            `/card/${ctx.card.id}`,
            'Open Card drawer'
        ).catch(console.error);
    }

    public setParams(ctx: TLPin, xPosition: number, yPosition: number, card: CardModel): void {
        ctx.xPosition = xPosition;
        ctx.yPosition = yPosition;
        ctx.card = card;
        ctx.pinSize = JaegerService.getPinSize(card);
        ctx.startDate = card?.startDate;
        ctx.endDate = card?.endDate;
    }

    public update(ctx: TLPin, xPosition: number, yPosition: number, card: CardModel): void {
        ctx.xPosition = xPosition;
        ctx.yPosition = yPosition;
        (window as any).bwPins.find((pin: any) => {
            return pin.card.id === ctx.card.id;
        }).card = card;
        this.setParams(ctx, xPosition, yPosition, card);
        this.upsertPinGroup(ctx);
        this.upsertEventLengthRect(ctx);
        this.upsertMainRect(ctx);
        this.upsertLine(ctx);
        this.createImage(ctx);
        if (ctx.startDate) {
            this.createAlertIcon(ctx);
            this.createPinDetails(ctx);
            this.createSavingsDetails(ctx);
        }
        ctx.pinGroup.show();
    }

    public updateX(ctx: TLPin): void {
        if (!ctx.startDate) return;
        const halfWidth: number = ctx.pinGroup.attrs.width / 2;
        const newX: number = UnitService.getJaegerX(
            ctx.timelineService.getProps().context,
            ctx.timelineService.getProps().user,
            ctx.startDate
        );
        ctx.xPosition = newX;
        ctx.pinGroup.x(newX - halfWidth);
        ctx.line?.x(newX - halfWidth);
        ctx.setDotPosition();
        this.upsertEventLengthRect(ctx);
    }

    private createImage(ctx: TLPin): void {
        const hasCustomImage: boolean = Boolean(ctx.card?.hash);
        let imageUrl: string;
        const size: number = hasCustomImage ? ctx.pinSize - 8 : ctx.pinSize - 16;
        const margin: number = hasCustomImage ? 4 : 8;

        /**
         * As of version 15.1, Safari's Canvas image implementation is broken. pin is a workaround that looks
         * marginally less broken but still pretty terrible.
         */
        if (isSafari) {
            imageUrl = hasCustomImage ? CardService.getPhotoUrl(ctx.card) : goalImagesSafari[ctx.card.description];
        } else {
            imageUrl = hasCustomImage ? CardService.getPhotoUrl(ctx.card) : goalImages[ctx.card.description];
        }

        const children: Konva.Node[] = ctx.pinGroup.getChildren((item) => item.attrs.bwType === 'image');
        let oldImage: Konva.Node = children.length > 0 && children[0];
        if (oldImage && (oldImage.attrs.url !== imageUrl || oldImage.width() !== size)) {
            children.forEach((child) => {
                // need to remove before destroy or else we get warnings
                child.remove();
                child.destroy();
            });
            oldImage = null;
        }

        if (!oldImage) {
            Konva.Image.fromURL(imageUrl, (imageNode: Group | Shape) => {
                imageNode.setAttrs({
                    x: margin,
                    y: margin,
                    width: size,
                    height: size,
                    perfectDrawEnabled: false,
                    willReadFrequently: true,
                    url: imageUrl,
                    bwType: 'image'
                });

                ctx.pinGroup.add(imageNode);
            });
        } else {
            oldImage.x(margin);
            oldImage.y(margin);
            oldImage.width(size);
            oldImage.height(size);
        }
    }

    private createPinDetails(ctx: TLPin): void {
        const name: string = ctx.card.nickname || LiteralsService.getLiteral('Goals.' + ctx.card.description);

        const cardMultiplier = _.get(ctx.card, 'attributes.icon') || EventSizes.DEFAULT;
        const blockSnapSize: number = ctx.timelineService.getBlockSnapSize();
        let textY = blockSnapSize * 0.2;

        switch (cardMultiplier) {
            case EventSizes.SMALL:
                textY = -blockSnapSize / 2;
                break;
            case EventSizes.LARGE:
                textY = blockSnapSize / 2;
                break;
        }

        const dateText: string = JaegerService.formatDates(
            ctx.timelineService.getProps().user.dob,
            ctx.card.startDate,
            ctx.card.endDate
        );

        // if we already have card details, only updated everything
        if (ctx.cardDetails) {
            ctx.cardDetails.x(ctx.pinSize);
            ctx.cardDetails.y(ctx.pinSize * 0.25);
            ctx.cardDetails.width(ctx.pinSize);
            ctx.cardDetails.height(ctx.pinSize * 0.75);
            ctx.cardDetails.attrs.card = ctx.card;
            ctx.cardName.text(name);
            ctx.cardName.y(textY);
            ctx.cardDates.y(textY + blockSnapSize * 2);
            ctx.cardDates.text(dateText);
            if (ctx.timelineService.getProps().showEventNames) {
                ctx.cardDetails.show();
            } else {
                ctx.cardDetails.hide();
            }
            if (ctx.card.endDate && ctx.card.startDate.isBefore(ctx.card.endDate)) {
                ctx.pinGroup.on(JaegerEvent.MOUSE_ENTER, () => {
                    ctx.eventLengthRect?.show();
                });

                ctx.pinGroup.on(JaegerEvent.MOUSE_LEAVE, () => {
                    ctx.eventLengthRect?.show();
                });
            } else {
                ctx.pinGroup.off(JaegerEvent.MOUSE_ENTER, () => {
                    ctx.eventLengthRect?.show();
                });

                ctx.pinGroup.off(JaegerEvent.MOUSE_LEAVE, () => {
                    ctx.eventLengthRect?.show();
                });
            }

            return;
        }

        ctx.cardDetails = new Konva.Group({
            x: ctx.pinSize,
            y: ctx.pinSize * 0.25,
            width: ctx.pinSize,
            height: ctx.pinSize * 0.75,
            perfectDrawEnabled: false,
            fill: '#fff',
            stroke: '#fff',
            card: ctx.card
        });

        ctx.cardName = new Konva.Text({
            x: blockSnapSize,
            y: textY,
            text: ctx.card.nickname || LiteralsService.getLiteral('Goals.' + ctx.card.description),
            fontSize: 14.22,
            fontStyle: 'bold',
            lineHeight: 1.5,
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fill: colours.cardDatesText,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false
        });

        ctx.cardDates = new Konva.Text({
            x: blockSnapSize,
            y: textY + blockSnapSize * 2,
            text: dateText,
            fontSize: 11.24,
            fontWeight: 500,
            lineHeight: 1.5,
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fill: colours.yearText,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false
        });

        const dragTexts = ctx.timelineService.getState().dragTexts;
        dragTexts[ctx.card.id] = ctx.cardDates;
        ctx.timelineService.setState({ dragTexts });

        ctx.cardDetails.add(ctx.cardName);
        ctx.cardDetails.add(ctx.cardDates);
        ctx.pinGroup.add(ctx.cardDetails);
        if (ctx.timelineService.getProps().showEventNames) {
            ctx.cardDetails.show();
        } else {
            ctx.cardDetails.hide();
        }

        const stage: Stage = ctx.timelineService.getStage();
        // when hovering, show card details
        ctx.pinGroup.on(JaegerEvent.MOUSE_ENTER, () => {
            stage.container().style.cursor = CursorStyle.MOVE;
            if (!ctx.timelineService.getProps().showEventNames) {
                ctx.cardDetails.show();
            }

            ctx.highlight(ctx.pinGroup);
            ctx.pinGroup.moveToTop();
        });

        // when leaving, hide card details
        ctx.pinGroup.on(JaegerEvent.MOUSE_LEAVE, () => {
            stage.container().style.cursor = CursorStyle.DEFAULT;
            if (!ctx.timelineService.getProps().showEventNames) {
                ctx.cardDetails.hide();
            }

            ctx.highlight();
        });

        if (ctx.card.endDate && ctx.card.startDate.isBefore(ctx.card.endDate)) {
            ctx.pinGroup.on(JaegerEvent.MOUSE_ENTER, () => {
                ctx.eventLengthRect?.show();
            });

            ctx.pinGroup.on(JaegerEvent.MOUSE_LEAVE, () => {
                ctx.eventLengthRect?.show();
            });
        }
    }

    private createSavingsDetails(ctx: TLPin): void {
        if (!ctx.card) return; // could be zero percent saved, check null
        if (ctx.card.percentSaved === undefined) {
            if (ctx.savingsDetails) {
                // if we were showing the bar before but there's no savings goal now, then hide it
                ctx.savingsDetails.hide();
            }
            return;
        }
        const x: number = ctx.pinSize * -0.15;
        const y: number = ctx.timelineService.getBlockSnapSize() * 0.25;
        const width: number = ctx.pinSize * 0.25;
        const height: number = ctx.pinSize * 0.9;
        const innerBarWidth: number = width - 4;
        const innerBarHeight: number = height - 4;
        const percentSaved = _.get(ctx.card, 'percentSaved', 0);
        const fullBarHeight: number = (innerBarHeight * percentSaved) / 100;

        if (ctx.savingsDetails) {
            ctx.savingsDetails.x(x);
            ctx.savingsDetails.y(y);
            ctx.savingsDetails.width(width);
            ctx.savingsDetails.height(height);
            ctx.savingsDetails.setAttrs({ ...ctx.savingsDetails.getAttrs(), card: ctx.card });
            const containers: Konva.Node[] = ctx.savingsDetails.getChildren(
                (child) => child.attrs.bwType === 'savingsContainer'
            );
            if (containers.length > 0) {
                containers[0].width(width);
                containers[0].height(height);
            }
            const backgroundBars: Konva.Node[] = ctx.savingsDetails.getChildren(
                (child) => child.attrs.bwType === 'backgroundBar'
            );
            if (backgroundBars.length > 0) {
                backgroundBars[0].width(innerBarWidth + 4);
                backgroundBars[0].height(innerBarHeight + 4);
            }
            const emptyBars: Konva.Node[] = ctx.savingsDetails.getChildren(
                (child) => child.attrs.bwType === 'emptyBar'
            );
            if (emptyBars.length > 0) {
                emptyBars[0].width(innerBarWidth);
                emptyBars[0].height(innerBarHeight);
            }
            const fullBars: Konva.Node[] = ctx.savingsDetails.getChildren((child) => child.attrs.bwType === 'fullBar');
            if (fullBars.length > 0) {
                fullBars[0].width(innerBarWidth);
                fullBars[0].height(fullBarHeight);
                fullBars[0].y(2 + innerBarHeight - fullBarHeight);
            }
            ctx.savingsDetails.show();

            return;
        }

        ctx.savingsDetails = new Konva.Group({
            x,
            y,
            width,
            height,
            perfectDrawEnabled: false,
            card: ctx.card
        });

        const savingsContainer: Rect = new Konva.Rect({
            x: 0,
            y: 0,
            width,
            height,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false,
            cornerRadius: 4,
            bwType: 'savingsContainer'
        });

        ctx.savingsDetails.add(savingsContainer);

        const backgroundBar: Rect = new Konva.Rect({
            x: 0,
            y: 0,
            width: innerBarWidth + 4,
            height: innerBarHeight + 4,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false,
            fill: '#ffffff',
            cornerRadius: 1,
            bwType: 'backgroundBar',
            shadowColor: 'rgba(200, 200, 200, .9)',
            shadowBlur: 12,
            shadowOffsetX: 0,
            shadowOffsetY: 4
        });

        ctx.savingsDetails.add(backgroundBar);

        const emptyBar: Rect = new Konva.Rect({
            x: 2,
            y: 2,
            width: innerBarWidth,
            height: innerBarHeight,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false,
            fill: colours.goodWeak,
            cornerRadius: 1,
            bwType: 'emptyBar'
        });

        ctx.savingsDetails.add(emptyBar);

        const fullBar: Rect = new Konva.Rect({
            x: 2,
            y: 2 + innerBarHeight - fullBarHeight,
            width: innerBarWidth,
            height: fullBarHeight,
            perfectDrawEnabled: false,
            shadowForStrokeEnabled: false,
            fill: colours.good,
            cornerRadius: [0, 0, 1, 1],
            bwType: 'fullBar'
        });

        ctx.savingsDetails.add(fullBar);
        ctx.pinGroup.add(ctx.savingsDetails);
    }

    private createAlertIcon(ctx: TLPin): void {
        // only create an alertIcon if we have a card and have income sources
        // note: this will change once we have alerts beyond income-related alerts
        if (!ctx.card || !canHaveIncomeSources(ctx.card)) return;

        // when a card is created, there is no warnings array
        // without any warnings, hide the alertIcon
        if (!ctx.card.warnings || ctx.card.warnings?.length === 0) {
            // hide the alertIcon if there are no warnings
            ctx.alertIcon?.hide();
            return;
        }
        // don't create a second alertIcon
        if (ctx.alertIcon) return;

        const x: number = ctx.pinSize * 0.9;
        const y: number = ctx.timelineService.getBlockSnapSize() * -0.5;
        const width: number = ctx.pinSize * 0.3;
        const height: number = ctx.pinSize * 0.3;

        ctx.alertIcon = new Konva.Group({
            x,
            y,
            width,
            height,
            perfectDrawEnabled: false,
            card: ctx.card
        });

        Konva.Image.fromURL(WarningIcon, (imageNode: Group | Shape) => {
            imageNode.setAttrs({
                x: 0,
                y: 0,
                width,
                height,
                perfectDrawEnabled: false,
                url: WarningIcon,
                bwType: 'image'
            });

            ctx.alertIcon.add(imageNode);
        });

        ctx.pinGroup.add(ctx.alertIcon);
    }

    public static getInstance(): PinFactory {
        if (!PinFactory.instance) {
            PinFactory.instance = new PinFactory();
        }

        return PinFactory.instance;
    }

    private setClickListener(ctx: TLPin): void {
        ctx.pinGroup.off(JaegerEvent.CLICK);
        ctx.pinGroup.on(JaegerEvent.CLICK, () => {
            this.onClick(ctx);
        });

        ctx.pinGroup.on(JaegerEvent.TOUCH_END, () => {
            ctx.pinGroup.moveToTop();
            NavigationService.goTo(
                ctx.timelineService.getProps().history,
                `/card/${ctx.card.id}`,
                'Open Card drawer'
            ).catch(console.error);
        });
    }

    private setContextMenuListener(ctx: TLPin): void {
        ctx.pinGroup.off(JaegerEvent.CONTEXT_MENU);
        ctx.pinGroup.on(JaegerEvent.CONTEXT_MENU, (event: KonvaEventObject<MouseEvent>) => {
            // Konva does not provide direct access to browser events here
            event.evt.preventDefault();
            const menuNode: HTMLElement = document.getElementById('eventContextMenu');
            const position: Vector2d = ctx.timelineService.getStage()?.getPointerPosition();
            // JQuery selectors
            const $smallNode: JQuery = $('#smallEventButton');
            const $defaultNode: JQuery = $('#defaultEventButton');
            const $largeNode: JQuery = $('#largeEventButton');
            const $deleteNode: JQuery = $('#deleteEventButton');
            const $checkMark: JQuery = $('.checkIcon');
            // show menu
            menuNode.style.display = 'flex';
            menuNode.style.top = `${position.y}px`;
            menuNode.style.left = `${position.x}px`;
            // kill extant listeners
            $smallNode.off();
            $defaultNode.off();
            $largeNode.off();
            $deleteNode.off();
            // reset checkmarks
            $checkMark.css({ visibility: 'hidden' });
            // add checkmark
            const cardMultiplier = _.get(ctx.card, 'attributes.icon') || EventSizes.DEFAULT;
            switch (cardMultiplier) {
                case EventSizes.SMALL:
                    $smallNode.find($checkMark).css({ visibility: 'visible' });
                    break;
                case EventSizes.LARGE:
                    $largeNode.find($checkMark).css({ visibility: 'visible' });
                    break;
                default:
                    $defaultNode.find($checkMark).css({ visibility: 'visible' });
            }
            // rewire listeners
            $smallNode.on(JaegerEvent.CLICK, () => {
                void ctx.updateCardSize(EventSizes.SMALL).then();
            });
            $defaultNode.on(JaegerEvent.CLICK, () => {
                void ctx.updateCardSize(EventSizes.DEFAULT).then();
            });
            $largeNode.on(JaegerEvent.CLICK, () => {
                void ctx.updateCardSize(EventSizes.LARGE).then();
            });
            $deleteNode.on(JaegerEvent.CLICK, () => {
                void ctx.timelineService.getProps().handleDeleteCard(ctx.card).then();
            });
        });
    }

    private setDragListener(ctx: TLPin): void {
        ctx.pinGroup.off(JaegerEvent.DRAG_START);
        ctx.pinGroup.on(JaegerEvent.DRAG_START, () => {
            // hide duration and savings on drag
            ctx.eventLengthRect?.hide();
            ctx.savingsDetails?.hide();
            ctx.timelineService.getTrash().show();
            ctx.pinGroup.moveToTop();
            ctx.timelineService.setState({
                xOriginal: ctx.pinGroup.x()
            });

            if (ctx.timelineService.pinBoard.dragFlag) {
                ctx.timelineService.pinBoard.dragFlag.hide();
            }
        });
    }

    private setDragEndListener(ctx: TLPin): void {
        ctx.pinGroup.off(JaegerEvent.DRAG_END);
        ctx.pinGroup.on(JaegerEvent.DRAG_END, () => {
            // show duration and savings on drag end
            ctx.eventLengthRect?.show();
            ctx.savingsDetails?.show();
            ctx.setDotPosition();
            ctx.pinGroup.moveToTop();
            ctx.timelineService.getTrash().hide();
            if (
                ctx.timelineService.isOverTrash(
                    ctx.pinGroup.x() + ctx.pinGroup.width() / 2,
                    ctx.pinGroup.y() + ctx.pinGroup.height() / 2
                )
            ) {
                ctx.timelineService.getProps().handleDeleteCard(ctx.card).catch(console.error);
                ctx.line?.destroy();
                ctx.dot?.destroyChildren();
                ctx.dot?.destroy();
                ctx.eventLengthRect?.destroy();
                ctx.line = null;
                ctx.dot = null;
                ctx.eventLengthRect = null;
                return;
            } else {
                ctx.timelineService.getStage()?.batchDraw();
                const rectOffset = ctx.pinSize / 2;
                const yPosition = JaegerService.getCardYPercent(
                    ctx.pinGroup.y(),
                    ctx.pinSize,
                    ctx.timelineService.getTimeline().timelineY
                );
                const newDate: LocalDate = UnitService.getJaegarDateFromPixels(
                    ctx.timelineService.getProps().context,
                    ctx.timelineService.getProps().user,
                    ctx.pinGroup.x() + rectOffset
                );
                ctx.timelineService
                    .getProps()
                    .onJaegerMove(
                        {
                            ...ctx.card,
                            yPosition
                        },
                        newDate
                    )
                    .then(() => ctx.cardDetails.show())
                    .catch((e) => console.error(e));
            }

            if (ctx.timelineService.getProps().dragFirstGoal) {
                ctx.timelineService.getProps().firstDrag();
            }
        });
    }

    private setDragMoveListener(ctx: TLPin): void {
        ctx.pinGroup.off(JaegerEvent.DRAG_MOVE);
        ctx.pinGroup.on(JaegerEvent.DRAG_MOVE, () => {
            ctx.setDotPosition();
            const trashRect: Rect = ctx.timelineService.getTrashRect();
            const trashText: Konva.Text = ctx.timelineService.getTrashText();
            trashText?.text('Delete event');

            if (
                ctx.timelineService.isOverTrash(
                    ctx.pinGroup.x() + ctx.pinGroup.width() / 2,
                    ctx.pinGroup.y() + ctx.pinGroup.height() / 2
                )
            ) {
                trashRect?.fill(colours.trashHover);
            } else {
                trashRect?.fill(colours.trashFill);
            }

            const duration = ctx.endDate ? ctx.startDate.until(ctx.endDate, ChronoUnit.MONTHS) : 0;
            if (ctx.timelineService.getState().xOriginal) {
                if (Math.abs(ctx.pinGroup.x() - ctx.timelineService.getState().xOriginal) > ctx.pinSize / 2) {
                    ctx.timelineService.setState({
                        xOriginal: 0
                    });
                } else {
                    ctx.pinGroup.x(ctx.timelineService.getState().xOriginal);
                }
            }
            if (ctx.pinGroup.x()) {
                ctx.line.x(ctx.pinGroup.x());
                ctx.line.y(ctx.pinGroup.y());
                ctx.line.points([
                    ctx.pinSize / 2,
                    ctx.pinSize + 13,
                    ctx.pinSize / 2,
                    ctx.timelineService.getTimeline().timelineY - ctx.pinGroup.y()
                ]);
                const x: number = ctx.pinSize / 2 + Number(ctx.pinGroup?.attrs?.x);
                const y: number = ctx.pinSize * 0.125 + Number(ctx.pinGroup?.attrs?.y);
                ctx.eventLengthRect?.x(x);
                ctx.eventLengthRect?.y(y);
            }

            const limits: { top: number; bottom: number } = JaegerService.getCardZoneYLimits(
                ctx.timelineService.getProps().height * ctx.timelineService.getProps().context.timelinePosition,
                ctx.pinSize
            );
            if (ctx.pinGroup.y() < limits.top) ctx.pinGroup.y(limits.top);
            if (ctx.pinGroup.y() > limits.bottom) ctx.pinGroup.y(limits.bottom);

            const rectOffset = ctx.pinSize / 2;
            const newDate = UnitService.getJaegarDateFromPixels(
                ctx.timelineService.getProps().context,
                ctx.timelineService.getProps().user,
                ctx.pinGroup.x() + rectOffset
            );
            const endDate = duration > 0 ? newDate.plusMonths(duration) : null;
            const dateText: string = JaegerService.formatDates(
                ctx.timelineService.getProps().user.dob,
                newDate,
                endDate
            );
            if (ctx.timelineService.getState().dragTexts[ctx.card.id]) {
                ctx.timelineService.getState().dragTexts[ctx.card.id].text(dateText);
            }

            ctx.timelineService.getStage()?.batchDraw();
        });
    }

    private upsertEventLengthRect(ctx: TLPin): void {
        if (ctx.endDate && ctx.startDate.isBefore(ctx.endDate)) {
            const x: number = ctx.pinSize / 2 + Number(ctx.pinGroup?.attrs?.x);
            const y: number = ctx.pinSize * 0.125 + Number(ctx.pinGroup?.attrs?.y);
            const width: number = UnitService.getJaegerPixelsBetweenDates(
                ctx.timelineService.getProps().context,
                ctx.timelineService.getProps().user,
                ctx.startDate,
                ctx.endDate
            );
            const height: number = ctx.pinSize * 0.75;
            if (ctx.eventLengthRect) {
                ctx.eventLengthRect.x(x);
                ctx.eventLengthRect.y(y);
                ctx.eventLengthRect.width(width);
                ctx.eventLengthRect.height(height);
            } else {
                ctx.eventLengthRect = new Rect({
                    x,
                    y,
                    width,
                    height,
                    fill: colours.brand,
                    opacity: 0.2,
                    perfectDrawEnabled: false,
                    shadowForStrokeEnabled: false,
                    cornerRadius: 4,
                    draggable: false
                });

                ctx.timelineService.getMainLayer().add(ctx.eventLengthRect);
            }

            ctx.eventLengthRect.zIndex(1);
        }
    }

    private upsertLine(ctx: TLPin): void {
        const triangleX: number = ctx.pinSize / 2;
        const triangleY: number = ctx.pinSize + 2;
        const innerTriangleX: number = ctx.pinSize / 2;
        const innerTriangleY: number = ctx.pinSize + 4;
        const points: number[] = [
            ctx.pinSize / 2,
            ctx.pinSize + 13,
            ctx.pinSize / 2,
            ctx.timelineService.getTimeline().timelineY - ctx.pinGroup.y()
        ];

        const dotY: number = ctx.timelineService.getTimeline().timelineY;
        const dotX: number = Number(ctx.pinGroup?.attrs?.x) + ctx.pinSize / 2;
        if (ctx.line) {
            // update triangle
            ctx.triangle.x(triangleX);
            ctx.triangle.y(triangleY);
            // update inner triangle
            ctx.innerTriangle.x(innerTriangleX);
            ctx.innerTriangle.y(innerTriangleY);
            // update line
            ctx.line.points(points);
            ctx.line.x(ctx.pinGroup.x());
            ctx.line.y(ctx.pinGroup.y());
            // update dot
            ctx.setDotPosition();
            ctx.line.show();
        } else {
            ctx.triangle = new RegularPolygon({
                x: triangleX,
                y: triangleY,
                rotation: 180,
                sides: 3,
                radius: 9,
                fill: '#fff',
                stroke: '#fff',
                strokeWidth: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                draggable: false
            });

            ctx.pinGroup.add(ctx.triangle);

            ctx.innerTriangle = new RegularPolygon({
                x: innerTriangleX,
                y: innerTriangleY,
                rotation: 180,
                sides: 3,
                radius: 5,
                fill: colours.income,
                stroke: '#fff',
                strokeWidth: 1,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            ctx.pinGroup.add(ctx.innerTriangle);

            ctx.line = new Line({
                points,
                x: ctx.pinGroup.attrs.x,
                y: ctx.pinGroup.attrs.y,
                stroke: colours.timeline,
                strokeWidth: 1,
                lineCap: 'round',
                lineJoin: 'round',
                dash: [6, 3],
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            ctx.timelineService.getMainLayer().add(ctx.line);

            ctx.dot = new Group({
                x: dotX,
                y: dotY,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            const outerCircle: Circle = new Circle({
                width: 12,
                fill: colours.backgroundLight,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });

            ctx.dot.add(outerCircle);
            const innerCircle: Circle = new Circle({
                width: 8,
                fill: colours.income,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            ctx.dot.add(innerCircle);

            ctx.timelineService.getMainLayer().add(ctx.dot);
        }
    }

    private upsertMainRect(ctx: TLPin): void {
        const width: number = ctx.pinSize;
        const height: number = ctx.pinSize;
        if (ctx.mainRect) {
            ctx.mainRect.width(width);
            ctx.mainRect.height(height);
        } else {
            ctx.mainRect = new Rect({
                x: 0,
                y: 0,
                width,
                height,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                fill: '#fff',
                stroke: '#fff',
                strokeWidth: 1,
                cornerRadius: 4,
                shadowColor: 'rgba(200, 200, 200, .9)',
                shadowBlur: 12,
                shadowOffsetX: 0,
                shadowOffsetY: 4
            });

            ctx.pinGroup.add(ctx.mainRect);
        }
    }

    private upsertPinGroup(ctx: TLPin): void {
        const x: number = ctx.xPosition - ctx.pinSize / 2;
        if (ctx.pinGroup) {
            ctx.pinGroup.x(x);
            ctx.pinGroup.y(ctx.yPosition);
            ctx.pinGroup.width(ctx.pinSize);
            ctx.pinGroup.height(ctx.pinSize);
            ctx.pinGroup.attrs.card = ctx.card;
        } else {
            ctx.pinGroup = new Group({
                x,
                y: ctx.yPosition,
                width: ctx.pinSize,
                height: ctx.pinSize,
                perfectDrawEnabled: false,
                fill: '#fff',
                stroke: '#fff',
                draggable: true,
                isPin: true,
                bwType: 'pin',
                card: ctx.card
            });
        }
    }
}
